import React, { useState } from "react";
import * as Mui from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import * as XLSX from "xlsx";
import { useSelector } from "react-redux";
import moment from "moment";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { designColors } from "services/constants/designTokens";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { tooltipStyleProps } from "services/constants/Styles";

const BULK_IMPORT_API =
  "team_zone/approve/attendance_correction/bulk-attendance-correction-import/";
const APPROVE_API =
  "team_zone/approve/attendance_correction/teamzone-attendance-correction/";

const SAMPLE_ROWS = [
  {
    "Employee ID": "959953",
    Date: "2026-09-02",
    "In Time": "22:00",
    "Out Time": "",
    Reason: "Forgot to punch out",
  },
  {
    "Employee ID": "959953",
    Date: "2026-09-03",
    "In Time": "",
    "Out Time": "06:00",
    Reason: "Missing punch out for the night shift",
  },
];

const rowField = (row, ...keys) => {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
      return row[key];
    }
  }
  return "";
};

const pad2 = (value) => String(value).padStart(2, "0");

const normalizeDateValue = (value) => {
  if (value === undefined || value === null || value === "") return "";

  if (typeof value === "number") {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) return `${pad2(parsed.d)}-${pad2(parsed.m)}-${parsed.y}`;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return moment(value).format("DD-MM-YYYY");
  }

  const parsed = moment(
    String(value).trim(),
    ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", "M/D/YY", "M/D/YYYY", "D/M/YY", "D/M/YYYY"],
    true,
  );
  return parsed.isValid() ? parsed.format("DD-MM-YYYY") : String(value).trim();
};

const normalizeTimeValue = (value) => {
  if (value === undefined || value === null || value === "") return "";

  if (typeof value === "number") {
    const totalMinutes = Math.round((value % 1) * 24 * 60);
    const minutesInDay = ((totalMinutes % 1440) + 1440) % 1440;
    return `${pad2(Math.floor(minutesInDay / 60))}:${pad2(minutesInDay % 60)}`;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return moment(value).format("HH:mm");
  }

  const parsed = moment(
    String(value).trim(),
    ["HH:mm", "H:mm", "HH:mm:ss", "hh:mm A", "h:mm A", "hh:mmA", "h:mmA"],
    true,
  );
  return parsed.isValid() ? parsed.format("HH:mm") : String(value).trim();
};

const normalizeImportRow = (row) => ({
  "Employee ID": String(rowField(row, "Employee ID", "employee_id", "EmployeeID")).trim(),
  Date: normalizeDateValue(rowField(row, "Date", "date")),
  "In Time": normalizeTimeValue(rowField(row, "In Time", "in_time", "InTime")),
  "Out Time": normalizeTimeValue(rowField(row, "Out Time", "out_time", "OutTime")),
  Reason: String(rowField(row, "Reason", "reason")).trim(),
});

const editableColumns = [
  { label: "Employee ID", key: "Employee ID", minWidth: 110 },
  { label: "Date", key: "Date", minWidth: 150, type: "date" },
  { label: "In Time", key: "In Time", minWidth: 100, placeholder: "HH:mm" },
  { label: "Out Time", key: "Out Time", minWidth: 100, placeholder: "HH:mm" },
  { label: "Reason", key: "Reason", minWidth: 200, placeholder: "Reason" },
];

// Rows carry dates as DD-MM-YYYY; the native date input needs YYYY-MM-DD.
const toDateInputValue = (value) => {
  const parsed = moment(value, "DD-MM-YYYY", true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const stickyActionCellSx = {
  position: "sticky",
  left: 0,
  zIndex: 1,
  backgroundColor: "background.paper",
  borderRight: `1px solid ${designColors.slate200}`,
  whiteSpace: "nowrap",
};

// Side-panel version of the app's established bulk-import pattern (see
// HR/EmployeeOnboard/BulkImport's Bank/Salary imports): the workbook is
// parsed client-side with the same `xlsx` package, POSTed for validation,
// reviewed in a table, then committed with a second call -- condensed into
// one panel (upload -> review -> submit) instead of a separate route, since
// Attendance Correction's own screens are entirely panel-based.
export const BulkAttendanceCorrectionImport = ({ open, onClose, setReCallApi, approveOnCreate = false }) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [draft, setDraft] = useState({});

  const downloadSample = () => {
    const ws = XLSX.utils.json_to_sheet(SAMPLE_ROWS);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sample");
    XLSX.writeFile(wb, "Sample Attendance Correction.xlsx");
  };

  const resetState = () => {
    setFileName("");
    setRows([]);
    setValidating(false);
    setSubmitting(false);
    setValidationMessage("");
    setEditingIndex(null);
    setDraft({});
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const validateRows = async (nextRows, message = "") => {
    setValidating(true);
    setValidationMessage(message);
    const response = await customAPI(`${domain}${BULK_IMPORT_API}`, "POST", {
      rows: nextRows,
      commit: false,
    });
    setValidating(false);
    setValidationMessage("");
    if (response?.ok) {
      setRows(response.responseDetails?.results || []);
      return true;
    }
    showToast("error", response?.message || "Couldn't validate the rows. Please try again.");
    return false;
  };

  const handleFile = (event) => {
    const file = event.target.files[0];
    event.target.value = null;
    if (!file) return;

    setFileName(file.name);
    setRows([]);
    setEditingIndex(null);
    setDraft({});

    const reader = new FileReader();
    reader.onload = async (e) => {
      let parsed = [];
      try {
        const workbook = XLSX.read(e.target.result, { type: "binary", cellDates: false });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        parsed = XLSX.utils
          .sheet_to_json(sheet, { raw: true, defval: "" })
          .map(normalizeImportRow);
      } catch (error) {
        showToast("error", "Couldn't read this file. Please check the format and try again.");
        return;
      }

      if (!parsed.length) {
        showToast("error", "The file has no rows.");
        return;
      }

      await validateRows(parsed, "Validating rows...");
    };
    reader.readAsBinaryString(file);
  };

  const hasInvalid = rows.some((row) => !row.is_valid);
  const isEditing = editingIndex !== null;
  const canSubmit = rows.length > 0 && !hasInvalid && !validating && !isEditing;

  const startEdit = (rowIndex) => {
    setEditingIndex(rowIndex);
    setDraft(normalizeImportRow(rows[rowIndex]));
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setDraft({});
  };

  const updateDraft = (key, value) => setDraft((previous) => ({ ...previous, [key]: value }));

  // Rows are validated together (the server pairs in/out punches across rows
  // into shifts), so any edit or removal re-validates the whole preview. The
  // edited row is marked invalid first so a failed validation call can't
  // leave an unchecked row submittable.
  const saveEdit = async () => {
    const nextRows = rows.map((row, index) =>
      index === editingIndex
        ? {
            ...normalizeImportRow(draft),
            is_valid: false,
            error: "Not validated yet. Edit and save this row again to retry.",
          }
        : row,
    );
    setRows(nextRows);
    setEditingIndex(null);
    setDraft({});
    await validateRows(nextRows.map(normalizeImportRow), "Validating changes...");
  };

  const deleteRow = async (rowIndex) => {
    const nextRows = rows.filter((_, index) => index !== rowIndex);
    setRows(nextRows);
    if (nextRows.length) {
      await validateRows(nextRows.map(normalizeImportRow), "Validating changes...");
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const response = await customAPI(`${domain}${BULK_IMPORT_API}`, "POST", {
      rows: rows.map(normalizeImportRow),
      commit: true,
    });
    if (!response?.ok) {
      setSubmitting(false);
      showToast("error", response?.message || "Couldn't submit the bulk upload. Please try again.");
      return;
    }

    const createdGroups = response.responseDetails?.created_groups || [];
    const createdCount = response.responseDetails?.created ?? createdGroups.length;

    if (!createdGroups.length) {
      setSubmitting(false);
      showToast("error", "No requests were created. Fix the invalid rows and try again.");
      return;
    }

    // An editor-only uploader can't approve: the requests stay Pending for an
    // approver to act on from the list.
    if (!approveOnCreate) {
      setSubmitting(false);
      showToast("success", `${createdCount} attendance correction request(s) submitted for approval.`);
      setReCallApi(true);
      handleClose();
      return;
    }

    // An approver bulk-uploading these on behalf of employees already IS the
    // approval -- same reasoning as the single "+ New Request" drawer's
    // approveOnCreate -- so approve each created request immediately instead
    // of leaving it Pending for a separate action. created_groups carries the
    // RESOLVED (user, anchor date) pairs -- these can differ from any single
    // uploaded row's own Date cell once rows are paired into shifts, so
    // there's no way to reconstruct them from the uploaded rows here.
    const approvalResults = await Promise.all(
      createdGroups.map((group) =>
        customAPI(`${domain}${APPROVE_API}`, "PUT", {
          user_id: group.user_id,
          date: group.date,
          status: "Approved",
        }),
      ),
    );
    setSubmitting(false);

    const approvedCount = approvalResults.filter((result) => result?.ok).length;
    const failedCount = createdGroups.length - approvedCount;

    if (failedCount > 0) {
      showToast(
        "error",
        `${createdCount} request(s) created, but ${failedCount} couldn't be approved automatically. ` +
          "Approve them from the list.",
      );
    } else {
      showToast("success", `${approvedCount} attendance correction request(s) approved.`);
    }
    setReCallApi(true);
    handleClose();
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={handleClose}
      title="Bulk Upload Attendance Correction"
      subtitle="Upload an Excel/CSV sheet to raise multiple correction requests at once."
      footer={
        <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <SecondaryButton onClick={handleClose} disabled={submitting}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleSubmit} disabled={!canSubmit} loading={submitting}>
            {rows.length ? `Submit (${rows.length})` : "Submit"}
          </PrimaryButton>
        </Mui.Stack>
      }
    >
      <Mui.Box sx={{ p: 2 }}>
        <Mui.Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
          <Mui.Button component="label" variant="outlined" startIcon={<CloudUploadOutlinedIcon />}>
            Choose File
            <input type="file" hidden accept=".xlsx,.xls,.csv" onChange={handleFile} />
          </Mui.Button>
          <Mui.Button
            variant="text"
            startIcon={<DownloadOutlinedIcon />}
            onClick={downloadSample}
          >
            Download Sample
          </Mui.Button>
        </Mui.Stack>
        <Mui.Typography sx={{ fontSize: 12.5, color: designColors.slate600, mb: 1.5 }}>
          Columns: Employee ID, Date (YYYY-MM-DD or DD-MM-YYYY), In Time (HH:MM), Out
          Time (HH:MM), Reason. Leave In Time or Out Time blank for a missing-punch
          correction.
        </Mui.Typography>

        {fileName ? (
          <Mui.Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>{fileName}</Mui.Typography>
        ) : null}

        {validating ? (
          <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1.5 }}>
            <Mui.CircularProgress size={18} />
            <Mui.Typography sx={{ fontSize: 13 }}>{validationMessage || "Validating rows..."}</Mui.Typography>
          </Mui.Stack>
        ) : null}

        {rows.length ? (
          <>
          <Mui.Typography sx={{ fontSize: 12.5, color: designColors.slate600, mb: 1 }}>
            Review the rows below. Use the edit icon to correct a row or the delete icon
            to drop it. Changes are re-validated when you save.
          </Mui.Typography>
          <Mui.TableContainer sx={{ maxHeight: 420, border: `1px solid ${designColors.slate200}`, borderRadius: 1 }}>
            <Mui.Table size="small" stickyHeader>
              <Mui.TableHead>
                <Mui.TableRow>
                  <Mui.TableCell sx={{ ...stickyActionCellSx, zIndex: 3 }}>Actions</Mui.TableCell>
                  {editableColumns.map((column) => (
                    <Mui.TableCell key={column.key} sx={{ minWidth: column.minWidth }}>
                      {column.label}
                    </Mui.TableCell>
                  ))}
                  <Mui.TableCell>Status</Mui.TableCell>
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {rows.map((row, idx) => {
                  const rowIsEditing = editingIndex === idx;
                  const actionsLocked = validating || submitting || (isEditing && !rowIsEditing);
                  return (
                  <Mui.TableRow
                    key={idx}
                    sx={rowIsEditing ? { backgroundColor: designColors.slateBg } : undefined}
                  >
                    <Mui.TableCell
                      sx={{
                        ...stickyActionCellSx,
                        ...(rowIsEditing ? { backgroundColor: designColors.slateBg } : {}),
                      }}
                    >
                      {rowIsEditing ? (
                        <>
                          <Mui.Tooltip title="Save" arrow slotProps={tooltipStyleProps}>
                            <Mui.IconButton size="small" color="success" onClick={saveEdit}>
                              <CheckIcon fontSize="small" />
                            </Mui.IconButton>
                          </Mui.Tooltip>
                          <Mui.Tooltip title="Cancel" arrow slotProps={tooltipStyleProps}>
                            <Mui.IconButton size="small" onClick={cancelEdit}>
                              <CloseIcon fontSize="small" />
                            </Mui.IconButton>
                          </Mui.Tooltip>
                        </>
                      ) : (
                        <>
                          <Mui.Tooltip title="Edit row" arrow slotProps={tooltipStyleProps}>
                            <span>
                              <Mui.IconButton
                                size="small"
                                color="primary"
                                onClick={() => startEdit(idx)}
                                disabled={actionsLocked}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </Mui.IconButton>
                            </span>
                          </Mui.Tooltip>
                          <Mui.Tooltip title="Remove row" arrow slotProps={tooltipStyleProps}>
                            <span>
                              <Mui.IconButton
                                size="small"
                                color="error"
                                onClick={() => deleteRow(idx)}
                                disabled={actionsLocked}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </Mui.IconButton>
                            </span>
                          </Mui.Tooltip>
                        </>
                      )}
                    </Mui.TableCell>
                    {editableColumns.map((column) => (
                      <Mui.TableCell key={column.key} sx={{ fontSize: 13 }}>
                        {rowIsEditing ? (
                          <Mui.TextField
                            size="small"
                            type={column.type || "text"}
                            value={
                              column.type === "date"
                                ? toDateInputValue(draft[column.key])
                                : draft[column.key] ?? ""
                            }
                            placeholder={column.placeholder}
                            onChange={(event) =>
                              updateDraft(
                                column.key,
                                column.type === "date"
                                  ? normalizeDateValue(event.target.value)
                                  : event.target.value,
                              )
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Enter") saveEdit();
                              if (event.key === "Escape") cancelEdit();
                            }}
                            sx={{
                              width: column.minWidth - 16,
                              "& input": { fontSize: 13, py: 0.75 },
                            }}
                          />
                        ) : (
                          rowField(row, column.key) || (
                            <Mui.Box component="span" sx={{ color: designColors.slate400 }}>
                              —
                            </Mui.Box>
                          )
                        )}
                      </Mui.TableCell>
                    ))}
                    <Mui.TableCell>
                      {row.is_valid ? (
                        <Mui.Chip size="small" label={row.status || "Valid"} color="success" variant="outlined" />
                      ) : (
                        <Mui.Tooltip title={row.error || "Invalid"} arrow slotProps={tooltipStyleProps}>
                          <Mui.Chip size="small" label="Invalid" color="error" variant="outlined" />
                        </Mui.Tooltip>
                      )}
                    </Mui.TableCell>
                  </Mui.TableRow>
                  );
                })}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.TableContainer>
          </>
        ) : null}
        {rows.length && hasInvalid ? (
          <Mui.Typography sx={{ fontSize: 12.5, color: "error.main", mt: 1 }}>
            Fix invalid rows in the preview, then validate before submitting.
          </Mui.Typography>
        ) : null}
      </Mui.Box>
    </GlobalSidePanel>
  );
};

export default BulkAttendanceCorrectionImport;
